<template>
	<app-title-bar :icon="['fas', 'fa-hand']" subtitle="Yard" title="Pick/Load List">
		<div class="flex flex-col-reverse sm:flex-row items-center gap-4">
			<depots-dropdown
				v-if="depots"
				v-model="depots"
				size="large"
				required
				update-user
				@update:model-value="getData" />
			<app-button to="/yard" icon="fa-circle-left"> Back </app-button>
		</div>
	</app-title-bar>

	<div class="sm:flex gap-4">
		<app-panel class="grow" title="Contract Details">
			<datatable
				v-model:server-options="params"
				:headers
				:value="items"
				:total
				:loading
				clickable
				@row-click="handleRowClick">
				<template #row-onHireProgress="{ row }">
					<div class="flex items-center">
						<font-awesome-icon
							:icon="
								getAssignedIconClass(row.onHireProgress.pick_progress_assignment)
							"
							class="w-4 text-xl" />
					</div>
				</template>
			</datatable>
		</app-panel>
	</div>
</template>

<script lang="ts" setup>
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'
	import { ref, onMounted, watch } from 'vue'
	import type { Header, Row } from '@/data/types/datatable'
	import ContractService from '@/services/contract'
	import useAuthStore from '@/stores/auth'
	import { useRouter } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import { formatItemsDates } from '@/utils/date'
	import { useDatatable } from '@/composables/datatable'
	import { getAssignedIconClass } from '@/utils/contract'

	// Resources
	const api = new ContractService()
	const toaster = useToasterStore()
	const router = useRouter()
	const auth = useAuthStore()
	const { items, total, params } = useDatatable()

	// Data
	const depots = ref<string | null>(null)
	const loading = ref(true)

	const headers: Header[] = [
		{ value: 'contract_number', text: 'Contract Number' },
		{ value: 'account_name', text: 'Account Name' },
		{ value: 'account', text: 'Account No' },
		{ value: 'pick_reference', text: 'Picking Reference' },
		{ value: 'item_count', text: 'Total Items' },
		{ value: 'item_weight', text: 'Total Weight (tonnes)' },
		{ value: 'delivery_date', text: 'Del Date' },
		{ value: 'site_name', text: 'Site Name' },
		{ value: 'site_address', text: 'Site Address' },
		{ value: 'onHireProgress', text: 'Progress' }
	]

	function handleRowClick(item: Row) {
		if (item && item.id) {
			router.push(`/yard/pick-item/${item.contract_number}`)
		}
	}

	const getData = () => {
		loading.value = true
		const page = params.value.page
		const per_page = params.value.rowsPerPage

		if (depots.value) {
			api.getYardWorkspaceArea('pick', depots.value!, page, per_page, null, {
				withItemCount: true,
				withProgress: true,
				withItemWeight: true
			})
				.then((res) => {
					items.value = formatItemsDates(res.data.data)
					if (res.data.meta && res.data.meta.paging) {
						total.value = Number(res.data.meta.paging.total)
					} else {
						total.value = 0
					}
					loading.value = false
				})
				.catch((error) => {
					console.log(error)
					toaster.error({ text: 'An error occurred retrieving contracts' })
					loading.value = false
				})
		} else {
			toaster.error({ text: 'Invalid depot' })
		}
	}

	watch(
		params,
		() => {
			getData()
		},
		{ deep: true }
	)

	onMounted(() => {
		if (auth.user && auth.user.current_depot) {
			depots.value = auth.user.current_depot
		}
		getData()
	})
</script>
